import { SEARCH_RESULT_SORT } from "@/constants";

export type Nullable<T> = T | null;

export interface Response<T> {
  data: T;
  description: string;
  message: string;
  status: number;
}

export interface ListData<T> {
  page: number;
  size: number;
  totalElement: number;
  totalPages: number;
  data: T[];
}

export type LoginBody = {
  username: string;
  password: string;
};

export interface IUser {
  isLogin: boolean;
}

export type ErrorMessage = {
  message: string;
  error: string;
  statusCode: number;
};

export type FetchEventParams = {
  page: number;
  size: number;
  query?: string;
  locationId?: string;
  startTime?: string;
  endTime?: string;
  sort?: "asc" | "desc";
  includeDeactive?: boolean;
};

export type EventItem = {
  id: string;
  createdAt: string;
  lastUpdatedAt: string;
  name: string;
  startTime: string;
  endTime: string;
  location: string;
  locationId: string;
  latitude: string;
  longitude: string;
  banner: string;
  featuredHotels: string[];
  description: string;
  active: boolean;
  suggestedAirports?: string;
};

export type DestinationItem = {
  destinationId: string;
  dataType: number;
  destinationName: Array<{
    cityName: string;
    countryCode: string;
    provinceCode: string;
    cityCode: string;
    name: string;
    language: string;
    hotelId: number;
    countryName: string;
    provinceName: string;
    latGoogle: number;
    lngGoogle: number;
  }>;
};

export type CreateEventBody = {
  name: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
  locationId: string;
  banner: string;
  latitude: string;
  longitude: string;
  featuredHotels: {
    id: string;
    price: number;
  }[];
  active: boolean;
  link: string;
  suggestedAirports?: string;
};

export type CreateEventForm = Omit<CreateEventBody, "featuredHotels"> & {
  featuredHotels: string[];
};

export interface IHotelItemData {
  id: number;
  thumbnail: string;
  name: string;
  rating: number;
  hotelScore: number;
  reviewsCount: number;
  district: string;
  priceSale: number;
  price: number;
  savePrice: number;
  percentDiscount: number;
  earnPTS: number;
  isFavorite: boolean;
  latitude: number;
  longitude: number;
}

export type SearchHotelItemData = {
  cityCode: string;
  cityName: string;
  hotelId: string;
  hotelName: string;
  hotelStar: number;
  hotelAddress: string;
  lowestPrice: number;
  saleCurrency: number;
  saleCurrencyCode: string;
  mainUrl: string;
  payAtHotelCurrency: Nullable<string>;
  longitude: number;
  latitude: number;
  lngGoogle: Nullable<number>;
  latGoogle: Nullable<number>;
  distance: number;
  hotelLabelNameList: Nullable<string>;
  finalPrice: number;
  discountPercent: number;
  hotelScore: number;
};

export type SearchHotelResponse = ListData<SearchHotelItemData>;

export type SearchHotelParam = {
  page: number;
  size: number;
  sortBy: SEARCH_RESULT_SORT;
  destinationId: string;
  startDate: string;
  endDate: string;
  guestCount: number;
  roomCount: number;
  childCount: number;
  priceBegin: string;
  priceEnd: string;
  hotelName: string;
  hotelPopular: Array<string>;
  hotelProperty: Array<string>;
  hotelStars: Array<string>;
  hotelRoomFacilities: Array<string>;
  hotelGeneralFacilities: Array<string>;
  hotelService: Array<string>;
};

export type HotelItem = {
  hotelId: string;
  hotelName: string;
};

export type DetailEvent = {
  id: string;
  createdAt: string;
  lastUpdatedAt: string;
  name: string;
  startTime: string;
  endTime: string;
  location: string;
  locationId: string;
  latitude: string;
  longitude: string;
  banner: string;
  featuredHotels: string[];
  featuredHotelsPrice: string[];
  description: string;
  active: boolean;
  link: null | string;
  suggestedAirports?: string;
};

export type SearchListHotelParams = {
  text: string;
  sortField?: "lowestPrice" | "hotelStar" | "hotelScore";
  sortOrder?: "asc" | "desc";
  limit?: number;
  page?: number;
};

export interface DiscountCity {
  cityCode: string;
  cityName: string;
  countryCode: string;
  countryName: string;
}

export type DiscountForm = {
  discountName: string;
  discountCode: string;
  discountValue: number;
  maximumDiscountValue?: number | null;
  minimumSpend?: number | null;
  discountType: DiscountType;
  usageLimit?: number | null;
  usageLimitPerUser?: number | null;
  startDate: string; // ISO 8601 format
  endDate: string; // ISO 8601 format
  active?: boolean;
  discountCities?: DiscountCity[];
  discountApplyFor?: DiscountApplyForType;
};

export type Discount = DiscountForm & {
  id: string;
  usedCount: number;
  isDeleted: boolean;
};

export type FetchDiscountParams = {
  page: number;
  size: number;
  field?: EFieldSortBy;
  order?: EOrderSortBy;
  query?: string;
  active?: string;
  discountType?: DiscountType;
  discountApplyFor?: DiscountApplyForType;
};
export interface FilterState {
  field: EFieldSortBy | null;
  order: EOrderSortBy | null;
  active: string | null;
  discountType: DiscountType | null;
}

export enum ActiveType {
  ALL = "All",
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

export enum Type {
  ALL = "All",
  PERCENTAGE = "Percentage",
  FIXED_AMOUNT = "Fixed",
}

export enum DiscountAmoutType {
  PERCENTAGE = "Percentage",
  FIXED_AMOUNT = "Fixed",
}

export enum DiscountType {
  PERCENTAGE = "PERCENTAGE",
  FLAT = "FLAT",
}
export enum EFieldSortBy {
  USAGE_LIMIT = "usageLimit",
  USAGE_LIMIT_PER_USER = "usageLimitPerUser",
}

export enum EOrderSortBy {
  ASC = "asc",
  DESC = "desc",
}

export type SortBy = {
  column: string;
  direction: EOrderSortBy;
};

export type Country = {
  countryId: string;
  countryCode: string;
  countryName: string;
};

export type City = {
  cityCode: string;
  cityName: string;
};

export type Province = {
  countryCode: string;
  countryName: string;
  provinceCode: string;
  citys: City[];
};

export type CountryResponse = {
  countries: Country[];
};

export type CityResponse = {
  provinces: Province[];
};

export type Referral = {
  date: string;
  total_points: number;
};

export enum ReferralSortBy {
  TOTAL_POINTS = "total_points",
  DATE = "date",
}

export type FetchReferralParams = {
  page: number;
  size: number;
  startDate: string;
  endDate: string;
  sortBy: ReferralSortBy;
  sortOrder: EOrderSortBy;
};

export type ReferralStatistic = {
  totalPoints: number;
  totalClaimedPoints: number;
  totalEarnedPoints: number;
};

export type Booking = {
  walletAddress: string;
  totalBookingPoints: number;
  totalReferralPoints: number;
  totalClaimedPoints: number;
  totalAvailablePoints: number;
};

export enum BookingSortBy {
  AVAILABLE_POINTS = "totalAvailablePoints",
  CLAIMED_POINTS = "totalClaimedPoints",
  TOTAL_BOOKING_POINTS = "totalBookingPoints",
  TOTAL_REFERRAL_POINTS = "totalReferralPoints",
}
export type FetchBookingParams = {
  page: number;
  size: number;
  sortBy: BookingSortBy;
  sortOrder: EOrderSortBy;
  query: string;
};

export type NetworkType = {
  key: ENetwork;
  value: ENetwork;
  label: string;
};

export enum ENetwork {
  ALL = "ALL",
  ETHEREUM = "EVM",
  SOLANA = "SOL",
  TON = "TON",
}

export const NetworkTypeOptions: NetworkType[] = [
  {
    key: ENetwork.ALL,
    value: ENetwork.ALL,
    label: "All Network",
  },
  {
    key: ENetwork.ETHEREUM,
    value: ENetwork.ETHEREUM,
    label: "Ethereum (ERC-20)",
  },
  { key: ENetwork.SOLANA, value: ENetwork.SOLANA, label: "Solana (SOL)" },
  // { key: ENetwork.TON, value: ENetwork.TON, label: "Ton (TON)" },
];

export enum ActionRefundType {
  ALL_ACTION = "ALL_ACTION",
  REVIEW = "PENDING",
  COMPLETE_REFUND = "APPROVED",
  REJECT = "REJECTED",
  COMPLETED = "COMPLETED",
}
export type ActionType = {
  key: ActionRefundType;
  value: ActionRefundType;
  label: string;
};
export const ActionRefundTypeOptions: ActionType[] = [
  {
    key: ActionRefundType.ALL_ACTION,
    value: ActionRefundType.ALL_ACTION,
    label: "All Action",
  },
  {
    key: ActionRefundType.REVIEW,
    value: ActionRefundType.REVIEW,
    label: "Review",
  },
  {
    key: ActionRefundType.COMPLETE_REFUND,
    value: ActionRefundType.COMPLETE_REFUND,
    label: "Complete Refund",
  },
  {
    key: ActionRefundType.REJECT,
    value: ActionRefundType.REJECT,
    label: "Reject",
  },
  {
    key: ActionRefundType.COMPLETED,
    value: ActionRefundType.COMPLETED,
    label: "Completed",
  },
];

export enum EFielRefundSortBy {
  AMOUNT_OF_REFUND = "totalRefundable",
  REQUEST_DATE = "requestDate",
}

export type FilterStateRefund = {
  network: ENetwork | null;
  action: ActionRefundType | null;
  startDate: string | null;
  endDate: string | null;
  sortBy: EFielRefundSortBy | null;
  sortOrder: EOrderSortBy | null;
};

export type FetchRefundParams = {
  page: number;
  size: number;
  sortBy?: EFielRefundSortBy;
  sortOrder?: EOrderSortBy;
  query?: string;
  network?: ENetwork;
  refundStatus?: ActionRefundType;
  startDate?: string;
  endDate?: string;
};

export enum RefundStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  COMPLETED = "COMPLETED",
}
export enum CancellationReason {
  CHANGE_OF_PLANS = "CHANGE_OF_PLANS",
  BOOKED_ELSEWHERE = "BOOKED_ELSEWHERE",
  INCONVENIENT_LOCATION = "INCONVENIENT_LOCATION",
  ERROR_IN_RESERVATION = "ERROR_IN_RESERVATION",
  UNFORESEEN_CIRCUMSTANCES = "UNFORESEEN_CIRCUMSTANCES",
}

export type Refund = {
  id: string;
  bookingCode: string;
  orderContactName: string;
  totalRefundable: number;
  walletAddress: string | null;
  refundNetwork: string | null;
  bankName: string | null;
  bankAccountName: string | null;
  bankAccountNumber: string | null;
  bookingDate: string;
  cancelRestrictionDay: number;
  cancelRestrictionTime: string;
  cancelRestrictionType: number;
  status: RefundStatus;
  cancellationReason: CancellationReason;
  requestDate: string;
  checkInDate: string;
  transactionId: string;
};

export type CancellationRefundBody = {
  id: string;
  status: RefundStatus;
  transactionId?: string;
};

export type FlightDiscountForm = {
  discountName: string;
  discountCode: string;
  discountValue: number;
  maximumDiscountValue?: number | null;
  minimumSpend?: number | null;
  discountType: DiscountType;
  usageLimit?: number | null;
  usageLimitPerUser?: number | null;
  startDate: string; // ISO 8601 format
  endDate: string; // ISO 8601 format
  active?: boolean;
  discountApplyFor?: DiscountApplyForType;
};

export type FlightDiscount = FlightDiscountForm & {
  id: string;
  usedCount: number;
  isDeleted: boolean;
};

export enum DiscountApplyForType {
  ALL = "ALL",
  HOTEL = "HOTEL",
  FLIGHT = "FLIGHT",
}

export enum FlightCancellationReason {
  VOLUNTARY = "VOLUNTARY",
  FLIGHT_CHANGE = "FLIGHT_CHANGE",
  SICKNESS_OR_DEATH = "SICKNESS_OR_DEATH",
  OTHER = "OTHER",
}

export enum FlightRefundStatus {
  PENDING_FLINK = "PENDING_FLINK",
  PENDING_ADMIN = "PENDING_ADMIN",
  PENDING_USER_CONFIRM = "PENDING_USER_CONFIRM",
  USER_APPROVED = "USER_APPROVED",
  // APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  COMPLETED = "COMPLETED",
  TIMEOUT_CLOSED = "TIMEOUT_CLOSED",
  FAILED = "FAILED",
}

export type FlightRefund = {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: FlightRefundStatus;
  flightOrderId: string;
  reason: FlightCancellationReason;
  reasonDetail: string;
  emailContact: string;
  type: string;
  network: ENetwork | null;
  walletAddress: string | null;
  transactionId: string | null;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
  bankName: string | null;
  refundedAt: string | null;
  refundAmount: number;
  refundAmountFlink: number;
  passengerIds: string[];
  segmentIds: string[];
  flightOrder: {
    id: string;
    createdAt: string;
    lastUpdatedAt: string;
    userId: string;
    orderId: string;
    partnerOrderId: string;
    parentOrderId: string;
    status: string;
    departureDate: string;
    arrivalDate: string;
    flightNumber: string;
    occupier: string;
    departureAirportCode: string;
    arrivalAirportCode: string;
    airlineCode: string;
    totalPaid: number;
  };
};

export type ActionFlightType = {
  key: FlightRefundStatus | "ALL";
  value: FlightRefundStatus | "ALL";
  label: string;
};

export const ActionFlightRefundTypeOptions: ActionFlightType[] = [
  {
    key: "ALL",
    value: "ALL",
    label: "All",
  },
  {
    key: FlightRefundStatus.PENDING_FLINK,
    value: FlightRefundStatus.PENDING_FLINK,
    label: "Flight Review",
  },
  {
    key: FlightRefundStatus.PENDING_USER_CONFIRM,
    value: FlightRefundStatus.PENDING_USER_CONFIRM,
    label: "User Confirm",
  },
  {
    key: FlightRefundStatus.PENDING_ADMIN,
    value: FlightRefundStatus.PENDING_ADMIN,
    label: "Review",
  },
  {
    key: FlightRefundStatus.USER_APPROVED,
    value: FlightRefundStatus.USER_APPROVED,
    label: "Complete Refund",
  },
  {
    key: FlightRefundStatus.REJECTED,
    value: FlightRefundStatus.REJECTED,
    label: "Reject",
  },
  {
    key: FlightRefundStatus.COMPLETED,
    value: FlightRefundStatus.COMPLETED,
    label: "Completed",
  },
  {
    key: FlightRefundStatus.TIMEOUT_CLOSED,
    value: FlightRefundStatus.TIMEOUT_CLOSED,
    label: "Timeout",
  },
  {
    key: FlightRefundStatus.FAILED,
    value: FlightRefundStatus.FAILED,
    label: "Failed",
  },
];

export enum EFielFlightRefundSortBy {
  AMOUNT_OF_REFUND = "flightOrder.totalPaid",
  REQUEST_DATE = "createdAt",
}

export type FilterStateFlightRefund = {
  network: ENetwork | null;
  action: FlightRefundStatus | "ALL" | null;
  startDate: string | null;
  endDate: string | null;
  sortBy: EFielFlightRefundSortBy | null;
  sortOrder: EOrderSortBy | null;
};

export type FetchFlightRefundParams = {
  page?: number;
  size?: number;
  startDate?: string;
  endDate?: string;
  refundStatus?: FlightRefundStatus;
  network?: ENetwork;
  sortBy?: EFielFlightRefundSortBy;
  sortOrder?: EOrderSortBy;
  query?: string;
  bankName?: string;
};

export enum ConfirmFlightRefundAction {
  APPROVE = "APPROVE",
  REJECT = "REJECT",
}

export type ConfirmFlightRefundBody = {
  id: string;
  action: ConfirmFlightRefundAction;
  refundAmount?: number;
};

export type CompleteFlightRefundBody = {
  id: string;
  txId: string;
};
